import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, statusMeta, depthMeta, numK } from '../data/sample';
import type { FunctionStatus } from '../data/sample';
import { state$, openFunc, openApp } from '../state';

const WASH: Record<FunctionStatus, string> = {
  won: '#fce9e3',
  contested: '#fdf3db',
  open: '#e4f4ec',
};
const WASH_HEAD: Record<FunctionStatus, string> = {
  won: '#f8dad0',
  contested: '#f8e5bc',
  open: '#d0ecdb',
};

const OPP_LEGEND = [
  { label: 'Open · opportunity', c: '#eef7f1', b: '#9bcfb1' },
  { label: 'Contested', c: '#fdf7ea', b: '#e8d28a' },
  { label: 'Won · no opportunity', c: '#fdf0ed', b: '#e6b6a8' },
];

function toggleMeta(on: boolean) {
  return { bg: on ? C.brand : '#fff', fg: on ? '#fff' : C.ink2, border: on ? C.brand : '#e1e3e5' };
}

export const FunctionMatrix = observer(function FunctionMatrix() {
  const colorByOpp = use$(state$.colorByOpp);
  const sortByOpp = use$(state$.sortByOpp);
  const defId = use$(state$.defId);
  const d = SAMPLE;

  const cb = toggleMeta(colorByOpp);
  const so = toggleMeta(sortByOpp);

  const fns = [...d.functions];
  if (sortByOpp) fns.sort((a, b) => b.opp.score - a.opp.score);
  else fns.sort((a, b) => a.order - b.order);
  const colIdx = fns.map((f) => d.functions.indexOf(f));

  const defFn = defId ? d.functions.find((x) => x.id === defId) : undefined;
  const defStatus = defFn ? statusMeta(defFn.status) : undefined;

  const depthLegend = [1, 2, 3].map((i) => ({ label: depthMeta(i).label, bg: depthMeta(i).bg }));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 3px' }}>
            App × Function Matrix
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: '#6d7175' }}>
            Click a cell to open the evidence behind it. Each column is labeled Won / Contested /
            Open.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => state$.colorByOpp.set(!colorByOpp)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 34,
              padding: '0 12px',
              border: `1px solid ${cb.border}`,
              background: cb.bg,
              borderRadius: 9,
              fontFamily: 'inherit',
              fontSize: 12.5,
              fontWeight: 600,
              color: cb.fg,
              cursor: 'pointer',
            }}
          >
            <span style={{ display: 'flex', gap: 2 }}>
              <span style={{ width: 8, height: 13, borderRadius: 2, background: '#10b981' }} />
              <span style={{ width: 8, height: 13, borderRadius: 2, background: '#f0b429' }} />
              <span style={{ width: 8, height: 13, borderRadius: 2, background: '#e0301e' }} />
            </span>{' '}
            Color columns by opportunity
          </button>
          <button
            onClick={() => state$.sortByOpp.set(!sortByOpp)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 34,
              padding: '0 12px',
              border: `1px solid ${so.border}`,
              background: so.bg,
              borderRadius: 9,
              fontFamily: 'inherit',
              fontSize: 12.5,
              fontWeight: 600,
              color: so.fg,
              cursor: 'pointer',
            }}
          >
            ↕ Sort columns by opportunity
          </button>
        </div>
      </div>

      {/* definition banner */}
      {defFn && defStatus && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e1e3e5',
            color: '#303030',
            borderRadius: 12,
            padding: '15px 18px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            boxShadow: '0 4px 16px rgba(26,29,36,0.06)',
            animation: 'nl-rise .25s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '4px 10px',
              borderRadius: 7,
              background: defStatus.bg,
              color: defStatus.fg,
              fontSize: 11,
              fontWeight: 700,
              flexShrink: 0,
              marginTop: 1,
            }}
          >
            {defStatus.label}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>{defFn.name}</div>
            <p style={{ margin: '0 0 6px', fontSize: 12.5, color: '#616161', lineHeight: 1.5 }}>
              {defFn.definition}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#8a8a8a', lineHeight: 1.5 }}>
              {defFn.statusWhy}
            </p>
          </div>
          <button
            onClick={() => state$.defId.set(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#8a8a8a',
              fontSize: 18,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* matrix */}
      <div
        style={{
          border: '1px solid #e1e3e5',
          borderRadius: 14,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <div style={{ overflow: 'auto', maxHeight: 560 }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
            <thead>
              <tr>
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    zIndex: 30,
                    background: '#fafbfb',
                    borderBottom: '1px solid #e1e3e5',
                    borderRight: '1px solid #e1e3e5',
                    padding: '11px 14px',
                    textAlign: 'left',
                    minWidth: 188,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6d7175',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    App ╲ Function
                  </span>
                </th>
                {fns.map((f) => {
                  const st = statusMeta(f.status);
                  return (
                    <th
                      key={f.id}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 20,
                        background: colorByOpp ? WASH_HEAD[f.status] : '#fafbfb',
                        borderBottom: '1px solid #e1e3e5',
                        borderRight: '1px solid #ebebeb',
                        padding: '10px 10px',
                        minWidth: 118,
                        verticalAlign: 'top',
                      }}
                    >
                      <div
                        onClick={() => openFunc(f.id)}
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.25,
                          marginBottom: 7,
                          cursor: 'pointer',
                          textAlign: 'left',
                          color: '#303030',
                        }}
                      >
                        {f.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 7px',
                            borderRadius: 6,
                            background: st.bg,
                            color: st.fg,
                            fontSize: 10,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {st.label}
                        </span>
                        <button
                          onClick={() => state$.defId.set(defId === f.id ? null : f.id)}
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: '1px solid #c9cccf',
                            background: '#fff',
                            color: '#6d7175',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          i
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {d.apps.map((a) => (
                <tr key={a.id}>
                  <th
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      background: '#fff',
                      borderBottom: '1px solid #ebebeb',
                      borderRight: '1px solid #e1e3e5',
                      padding: '9px 14px',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      onClick={() => openApp(a.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: a.iconBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: 12,
                          color: '#303030',
                          flexShrink: 0,
                        }}
                      >
                        {a.initials}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {a.name}
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: '#a5abb5',
                            fontFamily: "'JetBrains Mono'",
                          }}
                        >
                          {numK(a.reviews)} rv
                        </div>
                      </div>
                    </div>
                  </th>
                  {colIdx.map((fi) => {
                    const f = d.functions[fi]!;
                    const depth = a.depths[fi]!;
                    const sc = a.scores[fi];
                    const gated = a.gated[fi];
                    const dm = depthMeta(depth);
                    const bg = colorByOpp ? WASH[f.status] : '#fff';
                    return (
                      <td
                        key={f.id}
                        style={{
                          borderBottom: '1px solid #ebebeb',
                          borderRight: '1px solid #ebebeb',
                          padding: 0,
                          background: bg,
                        }}
                      >
                        <button
                          onClick={() => state$.drawer.set({ appId: a.id, funcId: f.id })}
                          style={{
                            width: '100%',
                            height: 54,
                            border: 'none',
                            background: bg,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            position: 'relative',
                            transition: 'filter .12s',
                          }}
                        >
                          {gated && (
                            <span
                              style={{
                                position: 'absolute',
                                top: 5,
                                right: 6,
                                fontFamily: "'JetBrains Mono'",
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: '#6d7175',
                                background: 'rgba(255,255,255,0.78)',
                                borderRadius: 4,
                                padding: '0 3px',
                              }}
                            >
                              $
                            </span>
                          )}
                          <div
                            style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 14 }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 6,
                                borderRadius: 1,
                                background: dm.bars[0],
                              }}
                            />
                            <span
                              style={{
                                width: 4,
                                height: 10,
                                borderRadius: 1,
                                background: dm.bars[1],
                              }}
                            />
                            <span
                              style={{
                                width: 4,
                                height: 14,
                                borderRadius: 1,
                                background: dm.bars[2],
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono'",
                              fontSize: 11,
                              fontWeight: 600,
                              color: sc === null ? '#bcbfc4' : depth >= 2 ? '#303030' : '#6d7175',
                            }}
                          >
                            {sc === null ? '—' : sc}
                          </span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* legend */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px 20px',
            flexWrap: 'wrap',
            padding: '12px 16px',
            borderTop: '1px solid #e1e3e5',
            background: '#fafbfb',
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#008060',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Opportunity (column color):
          </span>
          {OPP_LEGEND.map((o) => (
            <div
              key={o.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: '#616161',
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: o.c,
                  border: `1px solid ${o.b}`,
                }}
              />
              {o.label}
            </div>
          ))}
          <span style={{ width: 1, height: 15, background: '#e1e3e5' }} />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#6d7175',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Quality (gray):
          </span>
          {depthLegend.map((dl) => (
            <div
              key={dl.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: '#616161',
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: dl.bg,
                  border: '1px solid #e1e3e5',
                }}
              />
              {dl.label}
            </div>
          ))}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: '#8a8a8a', fontFamily: "'JetBrains Mono'" }}>
            <b style={{ color: '#303030' }}>$</b> = paid tier
          </span>
        </div>
      </div>
    </div>
  );
});
